import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useUser } from 'src/Hooks/useUser'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { isOwnerOrCompany } from 'src/Libs/installations/utils'
import { useTypedSelector } from 'src/State/RootReducer'
import { setInstallation } from 'src/State/Storage/StorageActions'
import useUserRoles from './user/use-user-roles'
import { getHttpStatusCodeFromError } from 'src/Libs/api/utils'
import { AuthFetchError } from '@e3dc-react/shell/Types'

interface UseActiveInstallationHook {
  id?: number

  /**
   * The current active Installation
   *
   * Undefined, if neither a matching active Installation was found, or the active Installation mismatches
   * with the provided `installationId`
   */
  installation?: Installation

  /**
   * True if the current active Installation can be accessed in any way and
   * the current authenticated user is the owner of this Installation, or has
   * an access-level of Service or higher.
   */
  userCanRead: boolean
  /**
   * True if the current active Installation can be accessed in any way and
   * the current authenticated user is the owner of this Installation, or has
   * an access-level of Service or higher.
   */
  userCanWrite: boolean
  /**
   * True if an Installation for the provided `installationId` is currently loaded
   */
  isLoading: boolean
  /**
   * True if an error occurred while loading the Installation
   */
  hasError: boolean
  errorStatus?: StatusCodes

  refreshInstallation: (installationId: string) => Promise<void>
}

const NOT_AUTHORIZED = [StatusCodes.UNAUTHORIZED]

/**
 * Provides accessors to the currently addressed Installation.
 *
 * This hook can be used anywhere, where an installation is required.
 *
 * But it should only be used on a Page level or the relevant root of a
 * nested Route structure.
 *
 *
 * **Usage example**
 * const {installationId} = useInstallationPages()
 * const {installation} = useActiveInstallation(false, installationId, storageId)
 *
 *
 * @param loadIfMissing If true, an Installation based on the given installationId is loaded
 * @param installationId
 * @param storageId
 */
const useActiveInstallation = (loadIfMissing = false, installationId?: string, storageId?: string): UseActiveInstallationHook => {
  const dispatch = useDispatch()
  const authFetch = useAuthFetchWithResult()
  const [requestProcessStatus, setRequestProcessStatus] = useState<StatusCodes | null>(null)
  const [requestProcessInstallationId, setRequestProcessInstallationId] = useState<string>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const storage = useTypedSelector(state => state.storage)
  const storagedInstallation = storage.installation
  const { userData } = useUser()
  const { isServiceUser } = useUserRoles()
  const currentRequestProcessStatus = requestProcessInstallationId === installationId ? requestProcessStatus : null

  const installation = useMemo<Installation | undefined>(() => {
    if (storagedInstallation?.id === Number(installationId)) {
      return storagedInstallation
    }
  }, [installationId, storagedInstallation])

  /**
   * Tells us if the current User has read access to the current addressed Installation
   *
   * Note:
   * Regardless that we use type-safety support through TypeScript, the fact that we blindly pass through api json
   * results as data models results in an unstable type-association.
   * With that in mind we have to do a manual type-cast for comparing the Identifier of an Installation and User
   * because it seems that both do not always be of a string or number, and so the check would fail without this
   * explicit type.
   */
  const userCanRead = useMemo<boolean>(() => {
    if (!installation || NOT_AUTHORIZED.includes(currentRequestProcessStatus as StatusCodes)) return false
    return isServiceUser || isOwnerOrCompany(installation, userData, true)
  }, [installation, currentRequestProcessStatus, isServiceUser, userData])

  const userCanWrite = useMemo<boolean>(() => {
    if (!installation || NOT_AUTHORIZED.includes(currentRequestProcessStatus as StatusCodes)) return false
    return isServiceUser || isOwnerOrCompany(installation, userData, true)
  }, [installation, currentRequestProcessStatus, isServiceUser, userData])

  const refreshInstallation = useCallback(
    async (installationId: string): Promise<void> => {
      setIsLoading(true)
      setHasError(false)
      setRequestProcessStatus(null)
      setRequestProcessInstallationId(installationId)
      try {
        const installation = await authFetch<Installation>({
          path: `installations/${installationId}`,
          endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
        })

        setHasError(false)
        setRequestProcessStatus(StatusCodes.OK)
        dispatch(setInstallation(installation))
      } catch (error) {
        const e = error as AuthFetchError
        console.error('error', e.response?.data)
        const statusCode = getHttpStatusCodeFromError(error)

        setHasError(true)
        setRequestProcessStatus(statusCode ?? null)
      } finally {
        setIsLoading(false)
      }
    },
    [authFetch, dispatch]
  )

  useEffect(() => {
    if (!loadIfMissing) return
    if (isLoading) return
    if (hasError && (currentRequestProcessStatus === StatusCodes.FORBIDDEN || requestProcessInstallationId === installationId)) return
    if (installationId && storagedInstallation?.id !== Number.parseInt(installationId)) {
      refreshInstallation(installationId).catch(console.error)
    }
  }, [
    currentRequestProcessStatus,
    hasError,
    installationId,
    isLoading,
    loadIfMissing,
    refreshInstallation,
    requestProcessInstallationId,
    storagedInstallation?.id,
  ])

  return {
    id: Number(installationId),
    installation,
    userCanRead,
    userCanWrite,
    isLoading,
    hasError,
    errorStatus: currentRequestProcessStatus ?? undefined,
    refreshInstallation,
  }
}

export default useActiveInstallation
